// Lib
import { useCallback } from 'react';

// Graph
import { authGroupCreate, authGroupUpdate } from '@graphql/mutations/group';
import { authIndex } from '@graphql/mutations/campaign';

// Api
import client from '@api';

// Types
import type {
  Auth,
  AuthGroup,
  GqlAuthSource,
  GqlMutationRootAuthGroupCreateArgs,
  GqlMutationRootAuthGroupUpdateArgs,
  GqlMutationRootAuthIndexArgs,
} from '@app-types/index';
import type {
  AuthGroupCreateResponse,
  AuthGroupUpdateResponse,
  AuthIndexResponse,
} from './types';

interface UseMasterGroupOperationsParams {
  groups: AuthGroup[];
  setGroups: React.Dispatch<React.SetStateAction<AuthGroup[]>>;
  loadingRef: React.MutableRefObject<boolean>;
}

export const useMasterGroupOperations = ({
  groups,
  setGroups,
  loadingRef,
}: UseMasterGroupOperationsParams) => {
  const onSaveInfluencerToMasterGroup = useCallback(
    async (
      auth: Auth & { innerId?: string; source?: string },
      setSelectedAuth?: (auth: Auth) => void,
      setMasterGroup?: (group: AuthGroup) => void,
    ): Promise<void> => {
      if (!loadingRef.current) {
        let addedAuthId = auth?._id;

        if (!auth?._id && auth?.innerId) {
          const { data } = await client().mutate<
            AuthIndexResponse,
            GqlMutationRootAuthIndexArgs
          >({
            mutation: authIndex,
            variables: {
              input: {
                innerId: auth?.innerId,
                source: auth?.source as GqlAuthSource,
              },
            },
          });

          if (data && data?.authIndex) {
            addedAuthId = data?.authIndex?._id || '';
            setSelectedAuth?.(data?.authIndex);
          }
        }

        if (!groups.find(({ name }) => name === 'master')) {
          const { data } = await client().mutate<
            AuthGroupCreateResponse,
            GqlMutationRootAuthGroupCreateArgs
          >({
            mutation: authGroupCreate,
            variables: {
              input: {
                name: 'master',
                ...(addedAuthId ? { authIds: [addedAuthId] } : {}),
              },
            },
          });
          if (data && data?.authGroupCreate) {
            setGroups((prev) => [data?.authGroupCreate, ...prev]);
            setMasterGroup?.(data?.authGroupCreate);
          }
        } else {
          const { data } = await client().mutate<
            AuthGroupUpdateResponse,
            GqlMutationRootAuthGroupUpdateArgs
          >({
            mutation: authGroupUpdate,
            variables: {
              input: {
                authGroupId: groups.find(({ name }) => name === 'master')?._id,
                addedAuthIds: addedAuthId ? [addedAuthId] : undefined,
              },
            },
          });

          if (data && data?.authGroupUpdate) {
            setGroups((prev) =>
              prev.map((item) =>
                item._id === data?.authGroupUpdate?._id
                  ? { ...item, authIds: data?.authGroupUpdate?.authIds }
                  : item,
              ),
            );
            setMasterGroup?.(data?.authGroupUpdate);
          }
        }
      }
    },
    [groups, setGroups, loadingRef],
  );

  const onSaveInfluencersToMasterGroup = useCallback(
    async (
      auths: Auth[],
      setMasterGroup?: (group: AuthGroup) => void,
    ): Promise<void> => {
      if (!loadingRef.current) {
        const addedAuthIds = auths.map(({ _id }) => _id);

        if (!groups.find(({ name }) => name === 'master')) {
          const { data } = await client().mutate<
            AuthGroupCreateResponse,
            GqlMutationRootAuthGroupCreateArgs
          >({
            mutation: authGroupCreate,
            variables: {
              input: {
                name: 'master',
                ...(addedAuthIds && addedAuthIds?.length
                  ? { authIds: addedAuthIds }
                  : {}),
              },
            },
          });
          if (data && data?.authGroupCreate) {
            setGroups((prev) => [data?.authGroupCreate, ...prev]);
            setMasterGroup?.(data?.authGroupCreate);
          }
        } else {
          const { data } = await client().mutate<
            AuthGroupUpdateResponse,
            GqlMutationRootAuthGroupUpdateArgs
          >({
            mutation: authGroupUpdate,
            variables: {
              input: {
                authGroupId: groups.find(({ name }) => name === 'master')?._id,
                addedAuthIds: addedAuthIds,
              },
            },
          });

          if (data && data?.authGroupUpdate) {
            setGroups((prev) =>
              prev.map((item) =>
                item._id === data?.authGroupUpdate?._id
                  ? { ...item, authIds: data?.authGroupUpdate?.authIds }
                  : item,
              ),
            );
            setMasterGroup?.(data?.authGroupUpdate);
          }
        }
      }
    },
    [groups, setGroups, loadingRef],
  );

  return {
    onSaveInfluencerToMasterGroup,
    onSaveInfluencersToMasterGroup,
  };
};
